import * as React from "react";
import {
    Box,
    Paper,
    Stack,
    Typography,
    Divider,
    Chip,
    Button,
    Grid,
    IconButton,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import DnsIcon from "@mui/icons-material/Dns";
import SettingsIcon from "@mui/icons-material/Settings";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { format } from "date-fns";
import { Localized } from "@fluent/react";

import { StructureName } from "../../device/Structure";
import { GampCategoryChip } from "../components/GampCategoryChip";
import {
    ComputerizedSystem,
    ComputerizedSystemCriticality,
} from "../computerizedSystemsApi";
import { GampCategory } from "../gampDictionary";

import { ObjectValidationStatusChip } from "../components/ObjectValidationStatusChip";
import { ObjectValidationStatus } from "../../project/validationProcess/validationProcessApi";
import { EditComputerizedSystemBasicInfoDialog } from "../dialogs/EditComputerizedSystemBasicInfoDialog";
import { EditComputerizedSystemVendorDialog } from "../dialogs/EditComputerizedSystemVendorDialog";
import { EditComputerizedSystemUsageDialog } from "../dialogs/EditComputerizedSystemUsageDialog";
import { EditComputerizedSystemValidationDialog } from "../dialogs/EditComputerizedSystemValidationDialog";
import EditIcon from "@mui/icons-material/Edit";
import { SuppliersSelector } from "../../suppliers/components/SuppliersSelector";
import { useAppDispatch } from "../../../app/hooks";
import { updateComputerizedSystemVendorInfo } from "../computerizedSystemsApi";
import { showError } from "../../notifications/notificationsSlice";

const dash = "—";
const fmtDate = (d?: Date | string | null) => {
    if (!d) return dash;
    const dt = typeof d === "string" ? new Date(d) : d;
    return isNaN(dt.getTime()) ? dash : format(dt, "dd-MM-yyyy");
};

const Section: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, mb: 2 }}>
        {children}
    </Paper>
);

type Props = {
    system: ComputerizedSystem;
    lang?: "pl" | "en";
    onUpdate: (patch: Partial<ComputerizedSystem>) => Promise<void> | void;
    onUpdated: (updated: ComputerizedSystem) => void;
};

export const ComputerizedSystemOverviewPanel: React.FC<Props> = ({
    system,
    lang = "pl",
    onUpdate,
    onUpdated,
}) => {
    const dispatch = useAppDispatch();
    const [openBasic, setOpenBasic] = React.useState(false);
    const [openUsage, setOpenUsage] = React.useState(false);
    const [openVendor, setOpenVendor] = React.useState(false);
    const [openValidation, setOpenValidation] = React.useState(false);
    const [openSupplierSelector, setOpenSupplierSelector] = React.useState(false);

    const CRIT_META: Record<
        ComputerizedSystemCriticality,
        { color: string; pl: string; en: string }
    > = {
        HIGH: { color: "#d32f2f", pl: "Wysoka", en: "High" },
        MEDIUM: { color: "#ed6c02", pl: "Średnia", en: "Medium" },
        LOW: { color: "#1976d2", pl: "Niska", en: "Low" },
    };

    const crit =
        (system.criticality?.toUpperCase?.() as ComputerizedSystemCriticality) ||
        undefined;
    const critMeta = crit ? CRIT_META[crit] : undefined;

    const handleSaved = async (patch: Partial<ComputerizedSystem>) => {
        await onUpdate(patch);
    };

    const handleSupplierSubmit = async (response: { supplierId: string; supplierName: string } | null) => {
        if (!response) return;

        const result = await dispatch(updateComputerizedSystemVendorInfo({
            id: system.id,
            vendor: system.vendor ?? "",
            softwareVersion: system.softwareVersion ?? "",
            supplierId: response.supplierId,
        }));

        if (updateComputerizedSystemVendorInfo.fulfilled.match(result)) {
            onUpdated(result.payload as ComputerizedSystem);
            setOpenSupplierSelector(false);
        } else {
            dispatch(showError("error"));
        }
    };

    return (
        <Box>
            {/* --- Podstawowe informacje --- */}
            <Section>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={(t) => ({
                        mb: 1,
                        bgcolor: "grey.100",
                        p: 1,
                        borderRadius: 1,
                        border: `1px solid ${t.palette.divider}`,
                    })}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                    >
                        <DnsIcon fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            <Localized id="cs-section-basic">
                                Podstawowe informacje
                            </Localized>
                        </Typography>
                    </Stack>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => setOpenBasic(true)}
                        sx={(t) => ({
                            textTransform: "none",
                            borderColor: t.palette.divider,
                            bgcolor: "background.paper",
                            "&:hover": {
                                borderColor: t.palette.info.main,
                                bgcolor: t.palette.action.hover,
                            },
                            gap: 0.5,
                        })}
                    >
                        <Localized id="edit">
                            <span>Edytuj</span>
                        </Localized>
                    </Button>

                </Stack>
                <Divider sx={{ mb: 1.5 }} />

                {/* tu zamiast Grid -> KeyValueRow */}
                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            <Localized id="cs-name">Nazwa</Localized>
                        </Typography>
                    }
                    value={system.name || dash}
                />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            <Localized id="cs-identifier">Numer identyfikacyjny</Localized>
                        </Typography>
                    }
                    value={system.computerizedSystemIdentifier || dash}
                />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            Struktura
                        </Typography>
                    }
                    value={
                        system.structureId ? (
                            <StructureName structureId={system.structureId} />
                        ) : (
                            dash
                        )
                    }
                />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            <Localized id="cs-description">Opis</Localized>
                        </Typography>
                    }
                    value={system.description || dash}
                    multiline
                />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            GAMP 5
                        </Typography>
                    }
                    value={
                        <GampCategoryChip
                            category={system.gampCategory as GampCategory}
                            lang={lang}
                            withTooltip
                        />
                    }
                />
            </Section>

            {/* --- Zastosowanie w firmie --- */}
            <Section>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={(t) => ({
                        mb: 1,
                        bgcolor: "grey.100",
                        p: 1,
                        borderRadius: 1,
                        border: `1px solid ${t.palette.divider}`,
                    })}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"

                    >
                        <BusinessIcon fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            <Localized id="cs-section-usage">
                                Zastosowanie w firmie
                            </Localized>
                        </Typography>
                    </Stack>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => setOpenUsage(true)}
                        sx={(t) => ({
                            textTransform: "none",
                            borderColor: t.palette.divider,
                            bgcolor: "background.paper",
                            "&:hover": {
                                borderColor: t.palette.info.main,
                                bgcolor: t.palette.action.hover,
                            },
                            gap: 0.5,
                        })}
                    >
                        <Localized id="edit">
                            <span>Edytuj</span>
                        </Localized>
                    </Button>
                </Stack>
                <Divider sx={{ mb: 1.5 }} />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            <Localized id="cs-department">Dział</Localized>
                        </Typography>
                    }
                    value={system.department || dash}
                />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            IT Owner
                        </Typography>
                    }
                    value={system.itOwnerId || dash}
                />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            Krytyczność dla firmy
                        </Typography>
                    }
                    value={
                        critMeta ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        bgcolor: critMeta.color,
                                    }}
                                />
                                <Typography component="span" sx={{ fontWeight: 500 }}>
                                    {crit}
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{ color: "text.secondary" }}
                                >
                                    {lang === "pl" ? critMeta.pl : critMeta.en}
                                </Typography>
                            </Stack>
                        ) : (
                            dash
                        )
                    }
                />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            GxP
                        </Typography>
                    }
                    value={
                        <Chip
                            size="small"
                            label={
                                system.isGxpRelevant
                                    ? lang === "pl"
                                        ? "TAK"
                                        : "YES"
                                    : lang === "pl"
                                        ? "NIE"
                                        : "NO"
                            }
                            color={system.isGxpRelevant ? "success" : "default"}
                            variant="outlined"
                        />
                    }
                />
            </Section>

            {/* --- Walidacja i zgodność --- */}
            <Section>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={(t) => ({
                        mb: 1,
                        bgcolor: "grey.100",
                        p: 1,
                        borderRadius: 1,
                        border: `1px solid ${t.palette.divider}`,
                    })}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                    >
                        <VerifiedUserIcon fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            <Localized id="cs-section-validation">
                                Walidacja i zgodność
                            </Localized>
                        </Typography>
                    </Stack>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon fontSize="small" />}
                        sx={(t) => ({
                            textTransform: "none",
                            borderColor: t.palette.divider,
                            bgcolor: "background.paper",
                            "&:hover": {
                                borderColor: t.palette.info.main,
                                bgcolor: t.palette.action.hover,
                            },
                            gap: 0.5,
                        })}
                        onClick={() => setOpenValidation(true)}
                    >
                        <Localized id="edit">
                            <span>Edytuj</span>
                        </Localized>
                    </Button>
                </Stack>
                <Divider sx={{ mb: 1.5 }} />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            <Localized id="cs-validation-status">
                                Status walidacji
                            </Localized>
                        </Typography>
                    }
                    value={
                        <ObjectValidationStatusChip
                            status={
                                (system.validationStatus as ObjectValidationStatus) ??
                                "NOT_APPLICABLE"
                            }
                            lang={lang}
                        />
                    }
                />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            <Localized id="cs-validation-date">
                                Data ostatniej walidacji
                            </Localized>
                        </Typography>
                    }
                    value={fmtDate(system.validationDate)}
                />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            <Localized id="cs-next-review-date">
                                Następny przegląd
                            </Localized>
                        </Typography>
                    }
                    value={fmtDate(system.nextReviewDate)}
                />
            </Section>

            {/* --- Dostawca i wersje --- */}
            <Section>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={(t) => ({
                        mb: 1,
                        bgcolor: "grey.100",
                        p: 1,
                        borderRadius: 1,
                        border: `1px solid ${t.palette.divider}`,
                    })}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                    >
                        <SettingsIcon fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            <Localized id="cs-section-vendor">
                                _Dostawca i wersje
                            </Localized>
                        </Typography>
                    </Stack>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon fontSize="small" />}
                        sx={(t) => ({
                            textTransform: "none",
                            borderColor: t.palette.divider,
                            bgcolor: "background.paper",
                            "&:hover": {
                                borderColor: t.palette.info.main,
                                bgcolor: t.palette.action.hover,
                            },
                            gap: 0.5,
                        })}
                        onClick={() => setOpenVendor(true)}
                    >
                        <Localized id="edit">
                            <span>Edytuj</span>
                        </Localized>
                    </Button>
                </Stack>
                <Divider sx={{ mb: 1.5 }} />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            Producent
                        </Typography>
                    }
                    value={
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                            <Typography component="span" variant="body1" fontWeight={600}>
                                {system.supplierName || dash}
                            </Typography>
                            <IconButton size="small" onClick={() => setOpenSupplierSelector(true)} aria-label="Edytuj producenta">
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    }
                />

                <KeyValueRow
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            <Localized id="cs-software-version">
                                Software version
                            </Localized>
                        </Typography>
                    }
                    value={system.softwareVersion || dash}
                />
            </Section>

            {/* --- Meta --- */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <KeyValueRow
                    label={
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CalendarMonthIcon fontSize="small" />
                            <Typography variant="body2" fontWeight={600}>
                                <Localized id="create-date">
                                    _Data utworzenia
                                </Localized>
                            </Typography>
                        </Stack>
                    }
                    value={fmtDate(system.createDate)}
                />
            </Paper>

            {/* --- DIALOGI --- */}
            {openBasic && (
                <EditComputerizedSystemBasicInfoDialog
                    computerizedSystem={system}
                    onCancel={() => setOpenBasic(false)}
                    onUpdate={(updated) => {
                        onUpdated(updated);
                        setOpenBasic(false);
                    }}
                />
            )}
            {openUsage && (
                <EditComputerizedSystemUsageDialog
                    computerizedSystem={system}
                    onCancel={() => setOpenUsage(false)}
                    onUpdate={(updated) => {
                        onUpdated(updated);
                        setOpenUsage(false);
                    }}
                />
            )}
            {openVendor && (
                <EditComputerizedSystemVendorDialog
                    computerizedSystem={system}
                    onCancel={() => setOpenVendor(false)}
                    onUpdate={(updated) => {
                        onUpdated(updated);
                        setOpenVendor(false);
                    }}
                />
            )}
            {openSupplierSelector && (
                <SuppliersSelector
                    selectedStructureId={system.structureId ?? null}
                    showFiltersTab
                    filterByStructure={Boolean(system.structureId)}
                    dialogOnly
                    open
                    dialogTitle="supplier-producer-selector-title"
                    value={system.supplierId ?? ""}
                    onSubmit={handleSupplierSubmit}
                    onCancel={() => setOpenSupplierSelector(false)}
                />
            )}
            {openValidation && (
                <EditComputerizedSystemValidationDialog
                    computerizedSystem={system}
                    onCancel={() => setOpenValidation(false)}
                    onUpdate={(updated) => {
                        onUpdated(updated);
                        setOpenValidation(false);
                    }}
                />
            )}
        </Box>
    );
};


type KeyValueRowProps = {
    label: React.ReactNode;  // <--- było: string
    value: React.ReactNode;
    multiline?: boolean;
    labelCols?: number; // domyślnie 2
    valueCols?: number; // domyślnie 10
};

const KeyValueRow = ({
    label,
    value,
    multiline = false,
    labelCols = 2,
    valueCols = 10,
}: KeyValueRowProps) => (
    <Grid
        container
        spacing={1.5}
        alignItems="stretch"
        sx={{
            py: 0.75,
            borderBottom: (t) => `1px dashed ${t.palette.divider}`,
            "&:last-of-type": { borderBottom: "none" },
        }}
    >
        {/* label */}
        <Grid item xs={12} sm={labelCols}>
            <Box
                sx={(t) => ({
                    bgcolor: t.palette.grey[100],
                    border: `1px solid ${t.palette.grey[200]}`,
                    borderRadius: 1,
                    px: 1,
                    py: 0.75,
                    height: "100%",
                    display: "flex",
                    alignItems: multiline ? "flex-start" : "center",
                })}
            >
                {/* jeśli label to string -> opakuj w Typography, w przeciwnym razie renderuj JSX */}
                {typeof label === "string" ? (
                    <Typography variant="body2" fontWeight={600} color="text.secondary">{label}</Typography>
                ) : (
                    label
                )}
            </Box>
        </Grid>

        {/* value */}
        <Grid item xs={12} sm={valueCols}>
            <Box
                sx={(t) => ({
                    border: `1px solid ${t.palette.grey[200]}`,
                    bgcolor: t.palette.grey[50],
                    borderRadius: 1,
                    px: 1.25,
                    py: 0.75,
                    height: "100%",
                    display: "flex",
                    alignItems: multiline ? "flex-start" : "center",
                })}
            >
                {React.isValidElement(value) ? value : (
                    <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={multiline ? { whiteSpace: "pre-line" } : undefined}
                    >
                        {value}
                    </Typography>
                )}
            </Box>
        </Grid>
    </Grid>
);
